/**
 * Iter Feb2026 · Task Approvals Tab
 *
 * Rendered as the second tab of /app/my/approvals. Approvers (RBAC code
 * `task.approve`) see every task in the workspace currently `pending`
 * or `changes_requested`. Non-approvers only see their own submissions
 * so they can chase.
 *
 * Actions: Approve / Request changes / Reject.
 * Budget shown from `est_amount` + `currency` (already on the task doc).
 * Approver notes are kept in the accounts-notes log — this tab only
 * writes to the tasks doc and the workspace activity log.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RotateCcw, Clock, Search, ShieldCheck, ShieldAlert, AlertTriangle, Inbox } from "lucide-react";
import { fmtRelative } from "@/lib/format";

const CURRENCY_SYMBOL = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };
const fmtMoney = (amt, cur = "INR") => {
  if (amt == null || amt === "") return "—";
  const sym = CURRENCY_SYMBOL[cur] || `${cur} `;
  const n = typeof amt === "number" ? amt : Number(amt);
  if (Number.isNaN(n)) return "—";
  return `${sym}${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

export default function TaskApprovalsTab() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [rows, setRows] = useState([]);
  const [canApprove, setCanApprove] = useState(false);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");
  const [action, setAction] = useState(null);   // {task, mode: 'approve'|'reject'|'changes'}
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!wsId) return;
    setBusy(true);
    try {
      // Iter Feb2026 · Fire-and-forget SLA nudge scan first so any
      // stale pending tasks get flagged (48h threshold) before we
      // fetch the list. Non-blocking — a failure here shouldn't hide
      // the queue from the approver.
      api.post(`/workspaces/${wsId}/tasks-pending-approval/nudge?hours=48`)
        .catch(() => {});
      const r = await api.get(`/workspaces/${wsId}/tasks-pending-approval`);
      setRows(r.data.tasks || []);
      setCanApprove(!!r.data.can_approve);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load task approvals");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((t) => (
      (t.title || "").toLowerCase().includes(term)
      || (t.project_name || "").toLowerCase().includes(term)
      || (t.created_by_name || "").toLowerCase().includes(term)
    ));
  }, [rows, q]);

  const pendingCount = rows.filter((t) => t.approval_status === "pending").length;
  const changesCount = rows.filter((t) => t.approval_status === "changes_requested").length;

  const submit = async () => {
    if (!action) return;
    const { task, mode } = action;
    if (mode !== "approve" && !note.trim()) {
      toast.error("A reason is required for this action.");
      return;
    }
    setSaving(true);
    try {
      const path = mode === "approve" ? `/tasks/${task.id}/approve`
        : mode === "reject" ? `/tasks/${task.id}/reject`
        : `/tasks/${task.id}/request-changes`;
      const body = mode === "approve" ? { note: note.trim() || null }
        : { reason: note.trim() };
      await api.post(path, body);
      toast.success(
        mode === "approve" ? "Task approved · execution unblocked"
        : mode === "reject" ? "Task rejected"
        : "Task sent back for changes"
      );
      setAction(null); setNote("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="task-approvals-tab">
      <div className="flex flex-wrap items-center gap-2 border border-border p-2 bg-muted/20">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 opacity-60" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search task, project or requester…"
            className="h-8 text-xs rounded-none pl-7"
            data-testid="task-approvals-search"
          />
        </div>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground pl-1">
          {canApprove ? (
            <><ShieldCheck className="w-3 h-3 inline-block mr-1" />Approver</>
          ) : (
            <>My submissions only</>
          )}
        </span>
        <span className="text-[11px] ml-auto" data-testid="task-approvals-count">
          <b className="text-foreground">{pendingCount}</b> pending
          {changesCount > 0 && <> · <b className="text-amber-600">{changesCount}</b> awaiting resubmit</>}
        </span>
      </div>

      {busy && (
        <div className="border border-border p-8 text-center text-sm text-muted-foreground" data-testid="task-approvals-loading">
          Loading task approvals…
        </div>
      )}
      {!busy && filtered.length === 0 && (
        <div className="border border-border p-8 text-center" data-testid="task-approvals-empty">
          <Inbox className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
          <div className="text-sm font-semibold">Nothing needs approval</div>
          <p className="text-xs text-muted-foreground mt-1">
            {canApprove
              ? "No tasks are currently awaiting your sign-off."
              : "You haven't submitted any tasks for approval."}
          </p>
        </div>
      )}
      {!busy && filtered.length > 0 && (
        <div className="border border-border">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-muted/40">
              <tr className="text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="px-3 py-2 text-left">Task</th>
                <th className="px-3 py-2 text-left w-40">Project</th>
                <th className="px-3 py-2 text-left w-32">Requested by</th>
                <th className="px-3 py-2 text-left w-28">Budget</th>
                <th className="px-3 py-2 text-left w-24">Submitted</th>
                <th className="px-3 py-2 text-left w-32">Status</th>
                <th className="px-3 py-2 text-right w-64">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} className="border-t border-border align-top hover:bg-muted/20"
                    data-testid={`task-approval-row-${t.id}`}>
                  <td className="px-3 py-2">
                    <div className="text-sm font-semibold">{t.title}</div>
                    {t.description && (
                      <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5"
                           dangerouslySetInnerHTML={{ __html: t.description }} />
                    )}
                    {t.approval_note && (
                      <div className="mt-1 text-[10px] text-amber-700 bg-amber-500/10 border border-amber-500/40 px-1.5 py-0.5 inline-block">
                        <AlertTriangle className="w-2.5 h-2.5 inline-block mr-0.5" />
                        {t.approval_note}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{t.project_name || <span className="text-muted-foreground italic">—</span>}</td>
                  <td className="px-3 py-2 text-xs">{t.created_by_name || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono tabular-nums">
                    {fmtMoney(t.est_amount, t.currency || "INR")}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-muted-foreground">
                    <Clock className="w-3 h-3 inline-block mr-0.5" />
                    {t.submitted_for_approval_at ? fmtRelative(t.submitted_for_approval_at) : "—"}
                    {/* Iter Feb2026 · SLA breach badge — auto-flagged
                        by the /nudge endpoint after 48h of silence.
                        After 72h without action the task is escalated
                        to the project owner (or workspace admin
                        fallback) and shows a purple "Escalated" chip. */}
                    {t.sla_breached && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-rose-500/60 bg-rose-500/10 text-rose-700 px-1.5 py-0.5"
                           data-testid={`task-approval-sla-breach-${t.id}`}
                           title={t.sla_first_nudge_at ? `First flagged ${fmtRelative(t.sla_first_nudge_at)}` : "Overdue for approval"}>
                        <AlertTriangle className="w-2.5 h-2.5" />
                        SLA · {t.sla_nudge_count > 1 ? `nudged ×${t.sla_nudge_count}` : "48h+"}
                      </div>
                    )}
                    {t.sla_escalated && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-purple-600/60 bg-purple-600/10 text-purple-800 px-1.5 py-0.5"
                           data-testid={`task-approval-sla-escalated-${t.id}`}
                           title={t.sla_escalated_at ? `Escalated ${fmtRelative(t.sla_escalated_at)}${t.sla_escalated_reason ? " · " + t.sla_escalated_reason.replace(/_/g, " ") : ""}` : "Escalated"}>
                        <ShieldAlert className="w-2.5 h-2.5" />
                        Escalated {t.sla_escalated_to_name ? `→ ${t.sla_escalated_to_name}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {t.approval_status === "pending" ? (
                      <span className="text-[10px] uppercase tracking-widest border border-amber-500/50 bg-amber-500/10 text-amber-700 px-1.5 py-0.5">
                        Pending
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest border border-rose-500/50 bg-rose-500/10 text-rose-700 px-1.5 py-0.5">
                        Changes requested
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canApprove ? (
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline"
                                className="h-7 rounded-none text-[11px] border-emerald-500/50 hover:bg-emerald-500/10 text-emerald-700"
                                onClick={() => { setAction({ task: t, mode: "approve" }); setNote(""); }}
                                data-testid={`task-approval-approve-${t.id}`}>
                          <CheckCircle2 className="w-3 h-3 mr-0.5" /> Approve
                        </Button>
                        <Button size="sm" variant="outline"
                                className="h-7 rounded-none text-[11px]"
                                onClick={() => { setAction({ task: t, mode: "changes" }); setNote(""); }}
                                data-testid={`task-approval-changes-${t.id}`}>
                          <RotateCcw className="w-3 h-3 mr-0.5" /> Changes
                        </Button>
                        <Button size="sm" variant="outline"
                                className="h-7 rounded-none text-[11px] border-rose-500/50 hover:bg-rose-500/10 text-rose-700"
                                onClick={() => { setAction({ task: t, mode: "reject" }); setNote(""); }}
                                data-testid={`task-approval-reject-${t.id}`}>
                          <XCircle className="w-3 h-3 mr-0.5" /> Reject
                        </Button>
                      </div>
                    ) : (
                      t.approval_status === "changes_requested" ? (
                        <Button size="sm" variant="outline"
                                className="h-7 rounded-none text-[11px]"
                                onClick={async () => {
                                  try {
                                    await api.post(`/tasks/${t.id}/submit-for-approval`);
                                    toast.success("Re-submitted for approval");
                                    await load();
                                  } catch (e) {
                                    toast.error(e?.response?.data?.detail || "Failed");
                                  }
                                }}
                                data-testid={`task-approval-resubmit-${t.id}`}>
                          Re-submit
                        </Button>
                      ) : (
                        <span className="text-[10px] text-muted-foreground italic">Awaiting approver</span>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!action} onOpenChange={(v) => { if (!v) { setAction(null); setNote(""); } }}>
        <DialogContent className="rounded-none" data-testid="task-approval-dialog">
          <DialogHeader>
            <DialogTitle>
              {action?.mode === "approve" ? "Approve task"
                : action?.mode === "reject" ? "Reject task"
                : "Request changes"}
            </DialogTitle>
          </DialogHeader>
          {action && (
            <div className="space-y-3 text-sm">
              <div className="p-2 border border-border bg-muted/40">
                <div className="font-semibold">{action.task.title}</div>
                <div className="text-[11px] text-muted-foreground">
                  {action.task.project_name || "—"} · Budget {fmtMoney(action.task.est_amount, action.task.currency || "INR")} · Requested by {action.task.created_by_name || "—"}
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  {action.mode === "approve" ? "Note (optional)" : "Reason (required)"}
                </label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={action.mode === "approve"
                    ? "Anything the assignee should know before starting…"
                    : "What needs to change / why is this blocked?"}
                  className="mt-1 rounded-none min-h-[100px] text-xs"
                  data-testid="task-approval-dialog-note"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => { setAction(null); setNote(""); }}
                    disabled={saving} data-testid="task-approval-dialog-cancel">
              Cancel
            </Button>
            <Button className="rounded-none" onClick={submit} disabled={saving}
                    data-testid="task-approval-dialog-submit">
              {saving ? "Saving…" : (
                action?.mode === "approve" ? "Approve" :
                action?.mode === "reject" ? "Reject" : "Send for changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
